/**
 * WI 35510 direct-call attachments, client side. The bridge enforces the same limits
 * (ConversationAttachmentRules); these mirror them so the composer can refuse a file
 * before uploading it. The server's answer is the authority.
 */
export interface ConversationAttachment {
  attachmentId: string;
  name: string;
  contentType: string;
  sizeBytes: number;
}

export interface AttachmentUpload {
  fileName: string;
  /** Base64 of the file bytes, without a data: prefix. */
  dataBase64: string;
}

export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_TURN = 10;

const ALLOWED_EXTENSIONS: Readonly<Record<string, string>> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  pdf: 'application/pdf',
  txt: 'text/plain',
  csv: 'text/csv',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // US 35617: video clips; the bridge checks the ISO-BMFF ftyp box.
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
};

const MESSAGES: Readonly<Record<string, string>> = {
  'attachment-too-large': `Files can be up to ${MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB.`,
  'attachment-type-not-allowed':
    'Photos (JPEG, PNG, GIF, WebP), videos (MP4, MOV), PDF, text, CSV and Office files can be attached.',
  'attachment-content-mismatch': "The file's contents do not match its type.",
  'attachment-empty': 'The file is empty.',
  'invalid-file-name': 'The file name is not valid.',
  'too-many-attachments': `A message can carry up to ${MAX_ATTACHMENTS_PER_TURN} attachments.`,
};

/** A user-facing sentence for a bridge or client attachment error code. Never echoes server text. */
export function attachmentErrorMessage(code: string | undefined): string {
  return (code && MESSAGES[code]) || 'The attachment could not be sent.';
}

/** The error code an upload failed with, when it carries one. */
export function attachmentErrorCode(error: unknown): string | undefined {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : undefined;
}

/** Decoded byte length of a base64 string (padding-aware, no decode). */
export function base64ByteLength(dataBase64: string): number {
  const trimmed = dataBase64.replace(/\s/g, '');
  const padding = trimmed.endsWith('==') ? 2 : trimmed.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((trimmed.length * 3) / 4) - padding);
}

/** Client pre-check: the error code the bridge would return for this file, or null. */
export function checkAttachmentUpload(upload: AttachmentUpload): string | null {
  const name = upload.fileName?.trim() ?? '';
  if (!name || /[\\/]/.test(name)) return 'invalid-file-name';
  const size = base64ByteLength(upload.dataBase64 ?? '');
  if (size === 0) return 'attachment-empty';
  if (size > MAX_ATTACHMENT_BYTES) return 'attachment-too-large';
  const dot = name.lastIndexOf('.');
  const extension = dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
  return ALLOWED_EXTENSIONS[extension] ? null : 'attachment-type-not-allowed';
}

/** Images render inline as a thumbnail; everything else is a named file card. */
export function isImageAttachment(
  value: Pick<ConversationAttachment, 'contentType'>
): boolean {
  return value.contentType.startsWith('image/');
}

/** US 35617: a video clip shows its still frame with a play overlay and plays on tap. */
export function isVideoAttachment(
  value: Pick<ConversationAttachment, 'contentType'>
): boolean {
  return value.contentType.startsWith('video/');
}

/** Shape check for attachment metadata from the bridge (events and upload responses). */
export function isConversationAttachment(
  value: unknown
): value is ConversationAttachment {
  const item = value as Partial<ConversationAttachment> | null;
  return (
    !!item &&
    typeof item.attachmentId === 'string' &&
    /^[a-f0-9]{32}$/i.test(item.attachmentId) &&
    typeof item.name === 'string' &&
    item.name.trim().length > 0 &&
    item.name.length <= 200 &&
    typeof item.contentType === 'string' &&
    item.contentType.length > 0 &&
    Number.isSafeInteger(item.sizeBytes) &&
    (item.sizeBytes as number) > 0
  );
}
